import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Building2, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import DemoBanner from "./DemoBanner";

const ORGS = [
  {
    key: "balm",
    name: "BALM",
    tag: "Employee Experience Showcase",
    description: "See what a polished, organization-specific CALLACHAP employee support experience can look like, along with the employer-side reporting demonstration.",
    best_for: "Showing the employee experience",
    cta: "Explore BALM Showcase",
    to: "/care-portal/demo/balm",
    active: true,
  },
  {
    key: "company-a",
    name: "Company A",
    tag: "Functional Demonstration",
    description: "Walk through working CALLACHAP actions, demonstrate the employee support workflow, and then see the aggregate employer reporting experience.",
    best_for: "Demonstrating the workflow",
    cta: "Open Functional Demo",
    to: "/care-portal/demo/company-a",
    active: true,
    live: true,
  },
  {
    key: "company-b",
    name: "Company B",
    tag: "Future Client",
    description: "Sample slot reserved for a future prospective organization.",
    active: false,
  },
];

export default function CareDemoHub() {
  useEffect(() => {
    document.title = "Care Portal — Demonstration Environments";
  }, []);

  return (
    <main data-testid="care-demo-hub" className="bg-white text-slate-900 min-h-screen">
      <DemoBanner
        title="CCCHAPS Care Portal Demonstration Environment"
        subtitle="Sample organizations only. No live employee data."
      />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold mb-3">
            For CCCHAPS presentations
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
            Care Portal — Demonstration Environments
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            Show prospective organizations both sides of the CCCHAPS Care Portal experience — what employees receive and how the support workflow and employer reporting operate.
          </p>
          <p className="mt-3 text-sm text-slate-500 leading-relaxed" data-testid="care-demo-hub-orientation">
            Choose an environment based on what you would like to demonstrate.
          </p>
        </div>

        <div className="mt-10 rounded-2xl p-5 md:p-6 bg-[#F0F4F8] border border-[#4A6FA5]/15 flex items-start gap-3 text-sm text-[#3B597B]" data-testid="care-demo-hub-isolation">
          <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={2.2} aria-hidden="true" />
          <p className="leading-relaxed">
            <strong>Demonstration Data Isolation:</strong> These demonstration environments are separated from live CCCHAPS Care Portal data. BALM uses simulated interactions, while Company A may create demonstration-only records within its isolated demo environment. No demonstration activity is associated with real employee, employer, support-request, or production Care Portal records.
          </p>
        </div>

        <div
          data-testid="care-demo-hub-orgs"
          className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {ORGS.map((o) =>
            o.active ? (
              <Link
                key={o.key}
                to={o.to}
                data-testid={`care-demo-hub-org-${o.key}`}
                className="group rounded-2xl p-6 md:p-7 bg-white border border-[#4A6FA5]/60 shadow-md hover:shadow-lg hover:border-[#4A6FA5] transition-all"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-[#4A6FA5]" strokeWidth={2} aria-hidden="true" />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">
                    Organization
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <h2 className="font-heading text-2xl tracking-tight text-slate-900">{o.name}</h2>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800">
                    <Sparkles className="w-3 h-3" strokeWidth={2.2} />
                    Demo
                  </span>
                </div>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] font-semibold text-[#4A6FA5]">{o.tag}</p>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed">{o.description}</p>
                {o.best_for && (
                  <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold" data-testid={`care-demo-hub-bestfor-${o.key}`}>
                    Best for: <span className="text-slate-700 normal-case tracking-normal font-medium">{o.best_for}</span>
                  </p>
                )}
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A6FA5] group-hover:text-[#3B597B]">
                  {o.cta || "Open demonstration"} <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
                </span>
              </Link>
            ) : (
              <div
                key={o.key}
                data-testid={`care-demo-hub-org-${o.key}`}
                className="rounded-2xl p-6 md:p-7 bg-slate-50 border border-dashed border-slate-300"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-slate-400" strokeWidth={2} aria-hidden="true" />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-400">
                    Organization
                  </span>
                </div>
                <h2 className="font-heading text-2xl tracking-tight text-slate-500">{o.name}</h2>
                <p className="mt-1 text-xs uppercase tracking-[0.2em] font-semibold text-slate-400">{o.tag}</p>
                <p className="mt-4 text-sm text-slate-500 leading-relaxed">{o.description}</p>
              </div>
            )
          )}
        </div>
      </section>
    </main>
  );
}
